import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { Physics } from './components/Physics'
import { GamesMenu } from './components/GamesMenu'

import './App.scss'
import moment from 'moment-timezone'
import { useUserData } from './user/useUserData'
import { insignias } from './constants/insignias'

export const App: React.FC = () => {
  const isDevEnvironment = process.env.NODE_ENV === 'development'
  const { daysRemaining, insigniaIndex } = useUserData()
  const insigniaSrc = insignias[insigniaIndex]
  const [devDaysRemaining, setDevDaysRemaining] = useState<number | null>(null)
  const displayDaysRemaining =
    isDevEnvironment && devDaysRemaining !== null ? devDaysRemaining : daysRemaining

  useEffect(() => {
    moment.locale('fi')
  }, [])

  const navigate = useNavigate()

  const openSettings = useCallback(() => {
    void navigate('/asetukset')
  }, [navigate])

  const openInfo = useCallback(() => {
    void navigate('/info')
  }, [navigate])

  const [animationKey, setAnimationKey] = useState(0)
  const [shouldPopSettingsButton, setShouldPopInfoButton] = useState(false)
  const [tjTitlePop, setTjTitlePop] = useState(false)

  const hasPreferencesKey = 'hasPreferences'
  const hasPreferences = localStorage.getItem(hasPreferencesKey)

  const pop = useCallback(() => {
    setTjTitlePop(true)
    window.setTimeout(() => setTjTitlePop(false), 1000)
  }, [setTjTitlePop])

  useEffect(() => {
    const hasVisitedKey = 'hasVisitedApp'
    const hasVisited = localStorage.getItem(hasVisitedKey)
    if (!hasVisited) {
      localStorage.setItem(hasVisitedKey, 'true')
      window.setTimeout(() => setShouldPopInfoButton(true), 2000)
      window.setTimeout(() => setShouldPopInfoButton(false), 3000)
    }
    pop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const runTJ = useCallback(() => {
    setAnimationKey((prevKey) => prevKey + 1)
    pop()
  }, [pop])

  const adjustDevDaysRemaining = useCallback(
    (delta: number) => {
      if (!isDevEnvironment) {
        return
      }
      setDevDaysRemaining((prev) => (prev ?? daysRemaining) + delta)
    },
    [daysRemaining, isDevEnvironment]
  )

  useEffect(() => {
    if (!isDevEnvironment) {
      return
    }
    setDevDaysRemaining(daysRemaining)
  }, [daysRemaining, isDevEnvironment])

  useEffect(() => {
    if (!isDevEnvironment) {
      return
    }

    const handleArrowKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        adjustDevDaysRemaining(-1)
      }
      if (event.key === 'ArrowRight') {
        adjustDevDaysRemaining(1)
      }
    }

    window.addEventListener('keydown', handleArrowKey)
    return () => {
      window.removeEventListener('keydown', handleArrowKey)
    }
  }, [adjustDevDaysRemaining, isDevEnvironment])

  const RightButton = () =>
    hasPreferences ? (
      <button type="button" className="infoButton" onClick={openInfo}>
        Käyttäjätiedot
      </button>
    ) : (
      <button
        type="button"
        className={`settingsButton ${shouldPopSettingsButton ? 'settingsButtonPop' : ''}`}
        onClick={openSettings}
      >
        Asetukset
      </button>
    )

  return (
    <div className="page homePage">
      <div className="header">
        <GamesMenu />
        <div className="titleContainer">
          <h1 className={`tjTitle ${tjTitlePop ? 'tjTitlePop' : ''}`} onClick={runTJ}>
            TJ {displayDaysRemaining}
          </h1>
          <img src={insigniaSrc} alt="Arvomerkki" className="insigniaIcon" />
        </div>
        <RightButton />
      </div>
      {isDevEnvironment ? (
        <div className="devTjControls" aria-label="Development TJ controls">
          <div className="devTjHeader">
            <span className="devTjLabel">Dev TJ</span>
          </div>
          <div className="devTjButtons">
            <button
              type="button"
              aria-label="Decrease TJ"
              onClick={() => adjustDevDaysRemaining(-1)}
            >
              -1
            </button>
            <span className="devTjValue">{displayDaysRemaining}</span>
            <button
              type="button"
              aria-label="Increase TJ"
              onClick={() => adjustDevDaysRemaining(1)}
            >
              +1
            </button>
          </div>
        </div>
      ) : null}
      <Physics key={animationKey} tj={displayDaysRemaining}>
        <p className="tagline">
          Aamukasa: varusmies, jolla on vielä paljon palvelusaikaa jäljellä.
        </p>
      </Physics>
    </div>
  )
}
